import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  EASE,
  EASE_PANEL,
  PANEL_DURATION,
  REVEAL_DURATION,
} from "./animation-config.js";

gsap.registerPlugin(ScrollTrigger);

const DURATION = PANEL_DURATION;
const REVEAL_Y = "100%";
const REVEAL_ROTATION = -90;
const OFFSET = "110%";

const reducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

function getDirection(from, to, count) {
  if (from === to) {
    return 0;
  }

  const forward = (to - from + count) % count;
  const backward = (from - to + count) % count;

  return forward <= backward ? 1 : -1;
}

function setActiveTab(tabs, index) {
  tabs.forEach((tab, tabIndex) => {
    const isActive = tabIndex === index;

    tab.classList.toggle("is-active", isActive);
    tab.setAttribute("aria-selected", isActive ? "true" : "false");
    tab.tabIndex = isActive ? 0 : -1;
  });
}

function showPanel(panel) {
  panel.hidden = false;
  panel.classList.add("is-active");
}

function hidePanel(panel) {
  panel.hidden = true;
  panel.classList.remove("is-active");
  gsap.set(panel, { clearProps: "all" });
}

function activateStep(widget, index, { animate = true } = {}) {
  const tabs = [...widget.querySelectorAll("[data-process-circle-step]")];
  const panels = [...widget.querySelectorAll("[data-process-circle-panel]")];
  const currentIndex = tabs.findIndex((tab) => tab.classList.contains("is-active"));

  if (currentIndex === index || !panels[index]) {
    return;
  }

  const outgoing = panels[currentIndex];
  const incoming = panels[index];
  const direction = getDirection(currentIndex, index, panels.length);

  setActiveTab(tabs, index);

  if (!animate || reducedMotion || !outgoing || direction === 0) {
    panels.forEach((panel, panelIndex) => {
      if (panelIndex === index) {
        showPanel(panel);
        gsap.set(panel, { x: 0, opacity: 1 });
        return;
      }

      hidePanel(panel);
    });
    return;
  }

  showPanel(incoming);
  gsap.set(incoming, { x: `${direction * 100}%`, opacity: 0 });

  gsap
    .timeline({
      defaults: { duration: DURATION, ease: EASE_PANEL },
      onComplete: () => {
        hidePanel(outgoing);
        gsap.set(incoming, { x: 0, opacity: 1 });
      },
    })
    .to(outgoing, { x: `${direction * -100}%`, opacity: 0 }, 0)
    .to(incoming, { x: 0, opacity: 1 }, 0);
}

function focusRelativeTab(tabs, fromIndex, offset) {
  const nextIndex = (fromIndex + offset + tabs.length) % tabs.length;
  tabs[nextIndex].focus();
  activateStep(tabs[nextIndex].closest(".process-circle__widget"), nextIndex);
}

function initProcessCircleReveal(section) {
  const widget = section.querySelector(".process-circle__widget");

  if (!widget) {
    return;
  }

  if (reducedMotion) {
    gsap.set(widget, { y: 0, rotation: 0, opacity: 1 });
    section.classList.add("is-in-view");
    return;
  }

  gsap.set(widget, {
    y: REVEAL_Y,
    rotation: REVEAL_ROTATION,
    opacity: 0,
  });

  ScrollTrigger.create({
    trigger: section,
    start: "top 70%",
    once: true,
    onEnter: () => {
      section.classList.add("is-in-view");

      gsap.to(widget, {
        y: 0,
        rotation: 0,
        opacity: 1,
        duration: REVEAL_DURATION,
        ease: EASE,
      });
    },
  });
}

function initProcessCircle(section) {
  const widget = section.querySelector(".process-circle__widget");

  if (!widget) {
    return;
  }

  const tabs = [...widget.querySelectorAll("[data-process-circle-step]")];

  if (!tabs.length) {
    return;
  }

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      activateStep(widget, Number(tab.dataset.processCircleStep));
    });

    tab.addEventListener("keydown", (event) => {
      const currentIndex = tabs.indexOf(tab);

      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          event.preventDefault();
          focusRelativeTab(tabs, currentIndex, 1);
          break;
        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault();
          focusRelativeTab(tabs, currentIndex, -1);
          break;
        case "Home":
          event.preventDefault();
          tabs[0].focus();
          activateStep(widget, 0);
          break;
        case "End":
          event.preventDefault();
          tabs[tabs.length - 1].focus();
          activateStep(widget, tabs.length - 1);
          break;
        default:
          break;
      }
    });
  });
}

document.querySelectorAll(".process-circle").forEach((section) => {
  initProcessCircleReveal(section);
  initProcessCircle(section);
});

window.addEventListener(
  "load",
  () => {
    ScrollTrigger.refresh();
  },
  { once: true },
);
